import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import { pinIcon, dotIcon } from "@/lib/leafletSetup";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/common/Loader";
import api from "@/lib/api";
import { Truck, Navigation, Camera, PenLine, CheckCircle2, XCircle, MapPin, Clock, ChevronLeft, LocateFixed, PackageCheck, FileDown } from "lucide-react";
import { openPdf } from "@/lib/pdf";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const c = ref.current, r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = ref.current.getContext("2d"); const p = pos(e); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#111827"; ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={520} height={160} data-testid="sig-canvas"
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
        className="w-full touch-none rounded-lg border-2 border-dashed border-[#c8cfda] bg-[#f9fafb]" />
      <button data-testid="sig-clear" onClick={clear} className="mt-1 text-xs text-[#0284C7] hover:underline">Clear signature</button>
    </div>
  );
}

export default function DriverApp() {
  const { user } = useAuth();
  const [routes, setRoutes] = useState(null);
  const [active, setActive] = useState(null);
  const [podStop, setPodStop] = useState(null);
  const [form, setForm] = useState({ recipient_name: "", notes: "", checklist: [], signature: null, photo_paths: [] });
  const [checklist, setChecklist] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/pod/routes?driver=${encodeURIComponent(user.email)}`);
    setRoutes(data);
    if (active) { const r = data.find((x) => x.id === active.id); if (r) setActive(r); }
  };
  useEffect(() => { api.get("/pod/checklist").then((r) => setChecklist(r.data)); load(); /* eslint-disable-next-line */ }, []);

  const openPod = (s) => { setForm({ recipient_name: "", notes: "", checklist: checklist.map((c) => ({ label: c, ok: false })), signature: null, photo_paths: [] }); setPodStop(s); };
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/pod/upload", fd); setForm((f) => ({ ...f, photo_paths: [...f.photo_paths, data.path] })); toast.success("Photo attached"); }
    catch { toast.error("Upload failed"); }
  };
  const submitPod = async () => {
    if (!form.recipient_name) { toast.error("Enter who received the goods"); return; }
    setBusy(true);
    try {
      await api.post(`/pod/routes/${active.id}/stops/${podStop.stop_id}/pod`, { ...form, delivered_qty: podStop.qty });
      toast.success(`POD captured for ${podStop.name}`); setPodStop(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const failStop = async (s) => { const reason = prompt("Failure reason (e.g. site closed, refused)"); if (!reason) return; await api.post(`/pod/routes/${active.id}/stops/${s.stop_id}/fail`, { reason }); toast.success("Marked as failed"); load(); };
  const shareGps = () => {
    if (!navigator.geolocation) { toast.error("Geolocation unavailable"); return; }
    navigator.geolocation.getCurrentPosition(async (p) => {
      await api.post(`/pod/routes/${active.id}/track`, { lat: p.coords.latitude, lng: p.coords.longitude });
      toast.success("Location shared"); load();
    }, () => toast.error("Location permission denied"));
  };

  if (routes === null) return <Loader label="Loading your deliveries…" />;

  // ---- route list ----
  if (!active) {
    return (
      <div data-testid="driver-home" className="mx-auto max-w-lg space-y-4">
        <div className="rounded-xl bg-[#0EA5E9] p-5 text-white shadow">
          <div className="flex items-center gap-2 text-sm opacity-90"><Truck size={18} /> Driver POD</div>
          <div className="mt-1 text-2xl font-bold">{user.name}</div>
          <div className="text-sm opacity-90">{routes.length} route{routes.length !== 1 ? "s" : ""} assigned</div>
        </div>
        {routes.length === 0 && <div className="rounded-xl border border-[#e5e9f0] bg-white p-8 text-center text-sm text-[#6a6d70]">No routes assigned yet. Your planner will dispatch runs here.</div>}
        {routes.map((r) => {
          const done = r.stops.filter((s) => s.status !== "PENDING").length;
          return (
            <button key={r.id} data-testid={`driver-route-${r.route_no}`} onClick={() => setActive(r)} className="w-full rounded-xl border border-[#e5e9f0] bg-white p-4 text-left shadow-sm hover:border-[#0EA5E9]">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-semibold text-[#0284C7]">{r.route_no}</span>
                <span className="rounded-full bg-[#e0f2fe] px-2 py-0.5 text-[11px] font-semibold text-[#0369a1]">{r.status}</span>
              </div>
              <div className="mt-1 text-sm text-[#32363a]">{r.vehicle_reg} · {r.stops.length} stops · {r.total_distance_km} km</div>
              <div className="mt-2 h-2 rounded bg-[#eef1f5]"><div className="h-2 rounded bg-[#10b981]" style={{ width: `${Math.round((done / r.stops.length) * 100)}%` }} /></div>
              <div className="mt-1 text-[11px] text-[#6a6d70]">{done}/{r.stops.length} completed</div>
            </button>
          );
        })}
      </div>
    );
  }

  // ---- active route ----
  const r = active;
  const veh = { lat: r.stops[0]?.lat, lng: r.stops[0]?.lng };
  const line = [...r.stops.map((s) => [s.lat, s.lng])];
  const next = r.stops.find((s) => s.status === "PENDING");
  return (
    <div data-testid="driver-route" className="mx-auto max-w-lg space-y-4">
      <button data-testid="driver-back" onClick={() => setActive(null)} className="inline-flex items-center gap-1 text-sm text-[#0284C7]"><ChevronLeft size={16} /> All routes</button>
      <div className="flex items-center justify-between">
        <div><div className="font-mono text-lg font-bold text-[#0284C7]">{r.route_no}</div><div className="text-xs text-[#6a6d70]">{r.vehicle_reg} · {r.total_distance_km} km · ETA {r.total_eta_min} min</div></div>
        <button data-testid="driver-gps" onClick={shareGps} className="inline-flex items-center gap-1 rounded-lg bg-[#EC4899] px-3 py-2 text-xs font-semibold text-white"><LocateFixed size={14} /> Share GPS</button>
      </div>

      <div className="h-56 overflow-hidden rounded-xl border border-[#e5e9f0]" data-testid="driver-map">
        <MapContainer center={[r.stops[0]?.lat || 52.48, r.stops[0]?.lng || -1.9]} zoom={10} style={{ height: "100%", width: "100%" }}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {r.stops.map((s, i) => <Marker key={s.stop_id} position={[s.lat, s.lng]} icon={pinIcon(String(i + 1), s.status === "DELIVERED" ? "#10b981" : s.status === "FAILED" ? "#ef4444" : "#0284C7")}><Popup>{s.name}</Popup></Marker>)}
          {line.length > 1 && <Polyline positions={line} pathOptions={{ color: "#0284C7", weight: 3, dashArray: "6 6" }} />}
          <Marker position={[veh.lat, veh.lng]} icon={dotIcon("#0EA5E9")} />
        </MapContainer>
      </div>

      {next && <div className="rounded-xl border-2 border-[#0EA5E9] bg-[#f0f9ff] p-3 text-sm"><span className="text-[11px] font-semibold uppercase text-[#0369a1]">Next stop</span><div className="font-semibold text-[#32363a]">{next.name}</div><div className="text-[11px] text-[#6a6d70]">{next.material_name} · {next.qty} {next.uom}</div></div>}

      <div className="space-y-3">
        {r.stops.map((s, i) => (
          <div key={s.stop_id} data-testid={`driver-stop-${i}`} className="rounded-xl border border-[#e5e9f0] bg-white p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${s.status === "DELIVERED" ? "bg-emerald-500" : s.status === "FAILED" ? "bg-red-500" : "bg-[#0284C7]"}`}>{i + 1}</span>
              <div className="flex-1">
                <div className="font-semibold text-[#32363a]">{s.name}</div>
                <div className="text-[11px] text-[#6a6d70]"><MapPin size={10} className="inline" /> {s.type === "COLLECTION" ? "Collection" : s.delivery_no} · {s.material_name} · {s.qty} {s.uom} · <Clock size={10} className="inline" /> {s.window}</div>
                {s.status === "DELIVERED" && <div className="mt-1 flex items-center gap-2"><span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600"><CheckCircle2 size={12} /> Signed by {s.pod?.recipient_name}</span><button data-testid={`driver-receipt-${i}`} onClick={() => openPdf(`/pod/routes/${active.id}/stops/${s.stop_id}/receipt`)} className="inline-flex items-center gap-1 rounded border border-[#c8cfda] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#0284C7]"><FileDown size={11} /> POD receipt</button></div>}
                {s.status === "FAILED" && <div className="mt-1 text-[11px] font-medium text-red-600">Failed: {s.pod?.failure_reason}</div>}
              </div>
            </div>
            {s.status === "PENDING" && (
              <div className="mt-3 flex gap-2">
                <a data-testid={`driver-nav-${i}`} href={`https://www.openstreetmap.org/directions?to=${s.lat}%2C${s.lng}`} target="_blank" rel="noreferrer" className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg border border-[#c8cfda] bg-white px-3 py-2 text-xs font-semibold text-[#0284C7]"><Navigation size={14} /> Navigate</a>
                <button data-testid={`driver-pod-${i}`} onClick={() => openPod(s)} className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg bg-[#10B981] px-3 py-2 text-xs font-semibold text-white"><PackageCheck size={14} /> Deliver</button>
                <button data-testid={`driver-fail-${i}`} onClick={() => failStop(s)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-red-300 bg-white px-3 py-2 text-xs font-semibold text-red-600"><XCircle size={14} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* POD capture sheet */}
      {podStop && (
        <div data-testid="driver-pod-modal" className="fixed inset-0 z-[1200] flex items-end justify-center bg-black/50 sm:items-center" onClick={() => setPodStop(null)}>
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 text-sm font-semibold text-[#32363a]">Proof of Delivery · {podStop.name}</div>
            <div className="space-y-3">
              <div className="rounded-lg border border-[#e5e9f0] p-3">
                <div className="mb-1.5 text-[11px] font-semibold uppercase text-[#6a6d70]">Delivery check-off</div>
                {form.checklist.map((c, idx) => (
                  <label key={idx} data-testid={`pod-check-${idx}`} className="flex items-center gap-2 py-1 text-sm">
                    <input type="checkbox" checked={c.ok} onChange={() => setForm((f) => { const cl = [...f.checklist]; cl[idx] = { ...cl[idx], ok: !cl[idx].ok }; return { ...f, checklist: cl }; })} />
                    <span className={c.ok ? "text-[#32363a]" : "text-[#6a6d70]"}>{c.label}</span>
                  </label>
                ))}
              </div>
              <input data-testid="pod-recipient" placeholder="Received by (name)" value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} className="w-full rounded-lg border border-[#e5e9f0] px-3 py-2 text-sm" />
              <textarea data-testid="pod-notes" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-lg border border-[#e5e9f0] px-3 py-2 text-sm" rows={2} />
              <div>
                <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase text-[#6a6d70]"><Camera size={13} /> Photos ({form.photo_paths.length})</div>
                <div className="flex flex-wrap gap-2">
                  {form.photo_paths.map((p) => <img key={p} src={`${BACKEND}/api/pod/file/${p}`} alt="pod" className="h-16 w-16 rounded-lg border border-[#e5e9f0] object-cover" />)}
                  <label data-testid="pod-photo-add" className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-[#c8cfda] text-[#6a6d70]"><Camera size={18} /><input type="file" accept="image/*" capture="environment" onChange={uploadPhoto} className="hidden" /></label>
                </div>
              </div>
              <div>
                <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase text-[#6a6d70]"><PenLine size={13} /> Customer signature</div>
                <SignaturePad onChange={(sig) => setForm((f) => ({ ...f, signature: sig }))} />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button data-testid="pod-cancel" onClick={() => setPodStop(null)} className="flex-1 rounded-lg border border-[#c8cfda] bg-white px-4 py-2.5 text-sm font-semibold text-[#32363a]">Cancel</button>
              <button data-testid="pod-submit" disabled={busy} onClick={submitPod} className="flex-[2] rounded-lg bg-[#10B981] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">Confirm delivery</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
